/* eslint-disable jsx-a11y/label-has-associated-control */

import React, { useState, useRef, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';

import xhr from '../../utils/xhr';
import utils from '../../utils/utils';
import { showNotification } from '../../redux/notification';
import { loadUser } from '../../redux/user';
import LoadingSpinner from '../../components/spinner/LoadingSpinner';
import backgroundImage from '../../assets/images/login-background.svg';
import logoIcon from '../../assets/images/portal-logo.svg';
import eyeOpenIcon from '../../assets/images/eye-purple.svg';
import eyeClosedIcon from '../../assets/images/eye-closed.svg';
import config from '../../config';

import './Login.scss';

const LOGIN_STEPS = {
	EMAIL: 'email',
	PASSWORD: 'password',
	TWO_FACTOR: 'two_factor'
};

const OldBrowserDetected = () => {
	return (
		<div className="old-browser-alert">
			<h1>Upgrade Your Browser</h1>
			<p>
				Please upgrade your browser for an optimal experience & improved security. We recommend{' '}
				<a href="https://www.google.com/chrome/">Google Chrome</a>.
			</p>
		</div>
	);
};

const Login = (props) => {
	const recaptchaRef = useRef(null);

	const dispatch = useDispatch();

	const [step, setStep] = useState(LOGIN_STEPS.EMAIL);

	const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
	const [email, setEmail] = useState('');
	const [emailError, setEmailError] = useState('');
	const [password, setPassword] = useState('');
	const [passwordInputType, setPasswordInputType] = useState('password');
	const [passwordError, setPasswordError] = useState('');
	const [verificationCode, setVerificationCode] = useState('');
	const [verificationCodeError, setVerificationCodeError] = useState('');
	const [needsRecaptcha, setNeedsRecaptcha] = useState(false);
	const [recaptchaResponse, setRecaptchaResponse] = useState(null);
	const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null);
	const [challengeToken, setChallengeToken] = useState(null);
	const [rememberDevice, setRememberDevice] = useState(false);

	const generateBrowserFingerprint = () => {
		return {
			userAgent: navigator.userAgent,
			platform: navigator.platform,
			language: navigator.language
		};
	};

	const resetRecaptcha = () => {
		if (recaptchaRef.current) {
			recaptchaRef.current.reset();
		}
		setRecaptchaResponse(null);
	};

	const togglePasswordInputType = () => {
		setPasswordInputType(passwordInputType === 'password' ? 'text' : 'password');
	};

	useEffect(() => {
		const urlParams = new URLSearchParams(window.location.search);
		const slotId = urlParams.get('slotId');
		const facilityId = urlParams.get('facilityId');
		if (slotId) {
			window.localStorage.setItem('slotId', slotId);
		}
		if (facilityId) {
			window.localStorage.setItem('activeFacility', facilityId);
		}
	}, []);

	const handleEmailSubmit = async (e) => {
		e.preventDefault();
		const trimmed = email.trim();
		if (!trimmed || !utils.emailRegex.test(trimmed)) {
			setEmailError('Enter a valid email address');
			return;
		}

		if (needsRecaptcha && !recaptchaResponse) {
			dispatch(showNotification('error', "Please confirm you're not a robot"));
			return;
		}

		try {
			setShowLoadingSpinner(true);
			const res = await xhr.request('POST', `/login/email`, {
				email: trimmed,
				recaptchaResponse
			});

			if (res.data && res.data.length > 0) {
				const result = res.data[0];
				window.location.href = `auth/${result.protocol.toLowerCase()}/login/${result.external_id}`;
				return;
			}

			setShowLoadingSpinner(false);
			setStep(LOGIN_STEPS.PASSWORD);
			resetRecaptcha();
		} catch (err) {
			setShowLoadingSpinner(false);
			const errs = err.response?.data?.errors;
			if (errs && errs.length) {
				dispatch(showNotification('error', errs[0].message));
			}
			if (errs && errs[0]?.needRecaptcha) {
				setNeedsRecaptcha(true);
				setRecaptchaResponse(null);
				setRecaptchaSiteKey(errs[0].recaptchaSiteKey);
				resetRecaptcha();
			}
		}
	};

	const handleSignIn = async (e) => {
		e.preventDefault();
		setPasswordError('');

		if (!password) {
			setPasswordError('This is a required field');
			return;
		}

		if (needsRecaptcha && !recaptchaResponse) {
			dispatch(showNotification('error', "Please confirm you're not a robot"));
			return;
		}

		try {
			setShowLoadingSpinner(true);

			const loginInfo = await xhr.request('POST', `/login`, {
				email: email.trim(),
				recaptchaResponse,
				password
			});

			if (loginInfo && loginInfo.data.challengeToken) {
				setChallengeToken(loginInfo.data.challengeToken);
				setShowLoadingSpinner(false);
				setStep(LOGIN_STEPS.TWO_FACTOR);
			} else {
				dispatch(loadUser());
				setShowLoadingSpinner(false);
				const queryParams = utils.getURLQueryParams();
				if (queryParams.url) {
					window.location.href = queryParams.url;
				} else {
					props.history.replace('/');
				}
			}

			resetRecaptcha();
		} catch (error) {
			setShowLoadingSpinner(false);
			const errs = error.response?.data?.errors;
			if (errs && errs.length) {
				if (
					errs[0].message.toLowerCase().includes('incorrect') ||
					errs[0].message.toLowerCase().includes('invalid') ||
					errs[0].message.toLowerCase().includes('not found')
				) {
					setPasswordError('Incorrect email or password');
					setPassword('');
				} else {
					dispatch(showNotification('error', errs[0].message));
				}

				if (errs && errs[0]?.needRecaptcha) {
					setNeedsRecaptcha(true);
					setRecaptchaResponse(null);
					setRecaptchaSiteKey(errs[0].recaptchaSiteKey);
					resetRecaptcha();
				}
			}
		}
	};

	const handleTwoFactorVerification = async (e) => {
		e.preventDefault();
		setVerificationCodeError('');

		if (!verificationCode.trim()) {
			setVerificationCodeError('This is a required field');
			return;
		}

		if (needsRecaptcha && !recaptchaResponse) {
			dispatch(showNotification('error', "Please confirm you're not a robot"));
			return;
		}

		try {
			setShowLoadingSpinner(true);

			const fingerprintData = rememberDevice ? generateBrowserFingerprint() : null;

			await xhr.request('POST', `/login/one-time-login`, {
				email: email.trim(),
				recaptchaResponse,
				verificationCode: verificationCode.trim(),
				challengeToken,
				rememberDevice,
				fingerprintData
			});

			dispatch(loadUser());
			const queryParams = utils.getURLQueryParams();
			if (queryParams.url) {
				window.location.href = queryParams.url;
			} else {
				props.history.replace('/');
			}
		} catch (error) {
			setShowLoadingSpinner(false);
			const [err] = error.response?.data?.errors || [];
			if (err) {
				if (err.needRecaptcha) {
					setNeedsRecaptcha(true);
					setRecaptchaResponse(null);
					setRecaptchaSiteKey(err.recaptchaSiteKey);
					resetRecaptcha();
				} else if (err.type === 'expired') {
					dispatch(
						showNotification('error', 'Login code expired. Please enter your password again to reattempt login.')
					);
				} else {
					setVerificationCodeError('Invalid or expired verification code');
					setVerificationCode('');
				}
			} else {
				dispatch(showNotification('error', 'Login code expired. Please enter your password again to reattempt login.'));
			}
		}
	};

	const handleResendCode = async () => {
		try {
			setShowLoadingSpinner(true);
			const res = await xhr.request('POST', `/login/one-time-password`, {
				email: email.trim(),
				challengeToken,
				recaptchaResponse
			});
			setChallengeToken(res.data.challengeToken);
			setShowLoadingSpinner(false);
			dispatch(showNotification('success', 'A new verification code has been sent to your email.'));
		} catch (error) {
			setShowLoadingSpinner(false);
			const [err] = error.response?.data?.errors || [];
			if (err) {
				if (err && err.needRecaptcha) {
					setNeedsRecaptcha(true);
					setRecaptchaResponse(null);
					setRecaptchaSiteKey(err.recaptchaSiteKey);
					resetRecaptcha();
				} else {
					dispatch(
						showNotification('error', 'Login code expired. Please enter your password again to reattempt login.')
					);
				}
			} else if (error.response?.status === 429) {
				dispatch(showNotification('error', error.response?.data?.message || 'Please try again later.'));
			} else {
				dispatch(showNotification('error', 'Login code expired. Please enter your password again to reattempt login.'));
			}
		}
	};

	const handleGoBack = () => {
		if (step === LOGIN_STEPS.TWO_FACTOR) {
			setStep(LOGIN_STEPS.PASSWORD);
			setVerificationCode('');
			setVerificationCodeError('');
		} else if (step === LOGIN_STEPS.PASSWORD) {
			setStep(LOGIN_STEPS.EMAIL);
			setPassword('');
			setPasswordError('');
		}
		setNeedsRecaptcha(false);
		resetRecaptcha();
	};

	const onSubmit = (e) => {
		e.preventDefault();
		if (step === LOGIN_STEPS.EMAIL) {
			handleEmailSubmit(e);
		} else if (step === LOGIN_STEPS.PASSWORD) {
			handleSignIn(e);
		} else if (step === LOGIN_STEPS.TWO_FACTOR) {
			handleTwoFactorVerification(e);
		}
	};

	return (
		<div className="login-screen">
			<img src={backgroundImage} className="login-background" alt="" />
			<div className="login-form-container">
				<form className="login-form" onSubmit={onSubmit} noValidate>
					<img src={logoIcon} alt="" className="logo" aria-hidden="true" onClick={() => props.history.replace('/')} />
					<h1 className="header">
						{step === LOGIN_STEPS.TWO_FACTOR ? 'Enter the 6–digit code sent to you at:' : 'Sign in'}
					</h1>

					{(step === LOGIN_STEPS.EMAIL || step === LOGIN_STEPS.PASSWORD) && (
						<div className="input-step">
							<label
								htmlFor="email"
								className={`input-step__label ${step === LOGIN_STEPS.PASSWORD ? 'input-step-label-disabled' : ''}`}
							>
								Email
							</label>
							<input
								id="email"
								type="email"
								maxLength={128}
								className={`input-step__input ${emailError ? 'is-error' : ''}`}
								placeholder="email@address.com"
								value={email}
								onChange={(e) => {
									setEmail(e.target.value);
									if (emailError) {
										setEmailError('');
									}
								}}
								disabled={step === LOGIN_STEPS.PASSWORD}
							/>
							{emailError && <div className="error-message">{emailError}</div>}
						</div>
					)}

					{step === LOGIN_STEPS.PASSWORD && (
						<>
							<div className="input-step">
								<label htmlFor="password" className="input-step__label">
									Password
								</label>
								<div className="input-password-wrapper">
									<input
										id="password"
										type={passwordInputType}
										maxLength={128}
										className={`input-step__input ${passwordError ? 'is-error' : ''}`}
										placeholder="Password"
										value={password}
										onChange={(e) => {
											setPassword(e.target.value);
											if (passwordError) {
												setPasswordError('');
											}
										}}
									/>
									<button
										type="button"
										onClick={togglePasswordInputType}
										className="password-toggle-button"
										aria-label={passwordInputType === 'password' ? 'Show password' : 'Hide password'}
									>
										<img
											src={passwordInputType === 'password' ? eyeOpenIcon : eyeClosedIcon}
											alt=""
											className="password-toggle-icon"
										/>
									</button>
								</div>
								{passwordError && <div className="error-message">{passwordError}</div>}
							</div>

							<div className="forgot-password-container">
								<Link to="/forgot-password" className="text-purple">
									Forgot password?
								</Link>
							</div>
						</>
					)}

					{step === LOGIN_STEPS.TWO_FACTOR && (
						<>
							<div className="two-factor-instructions">
								<p className="two-factor-email">{email}</p>
							</div>

							<div className="input-step">
								<label htmlFor="verificationCode" className="input-step__label">
									Verification code:
								</label>
								<input
									id="verificationCode"
									type="text"
									maxLength={6}
									className={`input-step__input ${verificationCodeError ? 'is-error' : ''}`}
									value={verificationCode}
									onChange={(e) => {
										setVerificationCode(e.target.value);
										if (verificationCodeError) {
											setVerificationCodeError('');
										}
									}}
								/>
								{verificationCodeError && <div className="error-message">{verificationCodeError}</div>}
							</div>
						</>
					)}

					{needsRecaptcha && (
						<ReCAPTCHA
							className="g-recaptcha"
							ref={recaptchaRef}
							sitekey={recaptchaSiteKey}
							size="normal"
							onChange={(e) => setRecaptchaResponse(e)}
						/>
					)}

					<div className="button-container">
						<Button size="lg" block type="submit" className="continue-button">
							{step === LOGIN_STEPS.TWO_FACTOR ? 'Verify' : 'Continue'}
						</Button>
						{(step === LOGIN_STEPS.PASSWORD || step === LOGIN_STEPS.TWO_FACTOR) && (
							<Button variant="outline-primary" size="lg" onClick={handleGoBack} className="go-back-button">
								Go back
							</Button>
						)}
					</div>

					{step === LOGIN_STEPS.TWO_FACTOR && (
						<div>
							<div className="remember-device-container">
								<label className="remember-device-checkbox">
									<input
										name="remember-device"
										type="checkbox"
										checked={rememberDevice}
										onChange={(e) => setRememberDevice(e.target.checked)}
									/>
									<span className="remember-device-text">Remember this device</span>
								</label>
							</div>
							<div className="two-factor-help">
								<div className="two-factor-resend">
									<p className="two-factor-help-text">Didn&apos;t receive it? </p>
									<Button variant="outline-primary" size="sm" onClick={handleResendCode} className="text-purple link">
										Get another code
									</Button>
								</div>
							</div>
						</div>
					)}
				</form>
			</div>
			{config.IS_IE && <OldBrowserDetected />}
			{showLoadingSpinner && <LoadingSpinner />}
		</div>
	);
};

export default withRouter(Login);
